import { useEffect, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchSalesForSelect } from '@/services/lookup';
import { savePayment, METHOD_LABELS, defaultStatusForMethod } from '@/services/payments';

interface PaymentModalProps {
  onClose: () => void;
  onSaved: () => void;
}

const methodKeys = Object.keys(METHOD_LABELS);

export default function PaymentModal({ onClose, onSaved }: PaymentModalProps) {
  const [sales, setSales] = useState<{ id: string; saleNumber: string }[]>([]);
  const [saleId, setSaleId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('transfer');
  const [transactionRef, setTransactionRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSalesForSelect().then((r) => setSales(r));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if ((Number(amount) || 0) <= 0) {
      setError('El monto debe ser mayor a cero.');
      return;
    }

    setSaving(true);
    const { error: saveError } = await savePayment({
      amount: Number(amount) || 0,
      method,
      status: defaultStatusForMethod(method),
      transactionRef,
      saleId,
    });
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal title="Nuevo Pago" subtitle="Registra un pago manual vinculado a una venta." onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Venta</label>
          <select className={inputCls} value={saleId} onChange={(e) => setSaleId(e.target.value)}>
            <option value="">Sin venta asociada</option>
            {sales.map((s) => (
              <option key={s.id} value={s.id}>{s.saleNumber}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Monto ($) *</label>
            <input type="number" min="0" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Referencia</label>
            <input className={inputCls} value={transactionRef} onChange={(e) => setTransactionRef(e.target.value)} placeholder="TX-123456" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Método</label>
            <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
              {methodKeys.map((m) => (
                <option key={m} value={m}>{METHOD_LABELS[m]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Estado (automático)</label>
            <div className="flex items-center gap-2 h-[38px] px-3 rounded-md bg-background-50 text-sm">
              {defaultStatusForMethod(method) === 'approved' ? (
                <span className="text-emerald-600 font-medium">Aprobado (pago en línea)</span>
              ) : (
                <span className="text-amber-600 font-medium">Pendiente (confirmar manualmente)</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>Crear pago</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}